// GA4 view_item_list / select_item for the cart drawer upsell shelf, in both collection and rules mode.
// The live "GA4 - Ecommerce Events" GTM tag already forwards both events and reads ecommerce.items
// from the data layer. It also fires on Shopify's own add_to_cart / view_item, so ecommerce is
// cleared before and after each push to stop shelf items leaking into those events. -- SG 20261005

export {};

const LIST_NAME = "cart_recs";
const DRAWER = ".section-preview-cart";
const SHELVES = [".cart-upsell__mobile", ".cart-upsell__desktop"];
const CARD = "[data-cart-rec]";
// A shelf counts as viewed once the drawer has been open and settled this long. Also absorbs the
// stale shelf shown between openPreviewCart() and the refreshed section HTML on add to cart.
const SETTLE_MS = 1000;

type Item = Record<string, string | number>;

let lastSignature = "";
let settleTimer: number | undefined;

function pushEcommerce(event: string, listId: string, currency: string, items: Item[]) {
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ ecommerce: null });
  w.dataLayer.push({
    event,
    ecommerce: { item_list_id: listId, item_list_name: LIST_NAME, currency, items },
  });
  w.dataLayer.push({ ecommerce: null });
}

function buildItem(card: HTMLElement, index: number, variant?: { id: string; title: string }): Item {
  const d = card.dataset;
  const item: Item = {
    item_id: `shopify_${d.cartRecCc}_${d.cartRecProductId}_${variant?.id || d.cartRecVariantId}`,
    item_name: d.cartRecName || "",
    item_brand: d.cartRecBrand || "",
    item_category: d.cartRecType || "",
    item_variant: variant?.title || d.cartRecVariantTitle || "",
    item_list_id: d.cartRecList || "",
    item_list_name: LIST_NAME,
    index,
    price: Number(d.cartRecPrice || 0) / 100,
    quantity: 1,
  };
  if (d.cartRecReason) item.cart_rec_reason = d.cartRecReason;
  return item;
}

function drawerOpen(): boolean {
  return !!document.querySelector(DRAWER)?.classList.contains("open");
}

// Both shelves are in the DOM; CSS hides one per breakpoint, so report the one with a box.
function visibleShelf(): HTMLElement | null {
  const drawer = document.querySelector(DRAWER);
  if (!drawer) return null;
  for (const selector of SHELVES) {
    const shelf = drawer.querySelector<HTMLElement>(selector);
    if (shelf && shelf.querySelector(CARD) && shelf.getClientRects().length > 0) return shelf;
  }
  return null;
}

function reportView() {
  if (!drawerOpen()) return;
  const shelf = visibleShelf();
  if (!shelf) return;
  const cards = Array.from(shelf.querySelectorAll<HTMLElement>(CARD));
  const items = cards.map((card, i) => buildItem(card, i + 1));
  const signature = items.map((item) => item.item_id).join(",");
  if (signature === lastSignature) return;
  lastSignature = signature;
  pushEcommerce("view_item_list", String(items[0].item_list_id), cards[0].dataset.cartRecCurrency || "", items);
}

function scheduleView() {
  window.clearTimeout(settleTimer);
  settleTimer = window.setTimeout(() => {
    try {
      reportView();
    } catch (error) {
      console.error("cart-recs-tracking view: ", error);
    }
  }, SETTLE_MS);
}

function onClick(event: Event) {
  const target = event.target as HTMLElement | null;
  const card = target?.closest<HTMLElement>(`${DRAWER} ${CARD}`);
  if (!card) return;
  const isAddToCart = !!target?.closest(".cart-upsell-variant-select-button");
  const isLink = !!target?.closest("a");
  if (!isAddToCart && !isLink) return;

  let variant: { id: string; title: string } | undefined;
  if (isAddToCart) {
    const select = card.querySelector<HTMLSelectElement>("select");
    if (!select?.value) return;
    variant = { id: select.value, title: select.selectedOptions[0]?.textContent?.trim() || "" };
  }
  const shelfCards = Array.from(card.parentElement?.querySelectorAll<HTMLElement>(`:scope > ${CARD}`) || []);
  const index = Math.max(shelfCards.indexOf(card), 0) + 1;
  const item = buildItem(card, index, variant);
  pushEcommerce("select_item", String(item.item_list_id), card.dataset.cartRecCurrency || "", [item]);
}

function init() {
  const drawer = document.querySelector(DRAWER);
  if (!drawer) return;

  new MutationObserver(() => {
    if (drawerOpen()) {
      scheduleView();
    } else {
      window.clearTimeout(settleTimer);
      lastSignature = "";
    }
  }).observe(drawer, { attributes: true, attributeFilter: ["class"] });

  document.addEventListener("cart:updated", scheduleView);
  // Capture phase: the add-to-cart handler re-renders the drawer, which would detach the card.
  document.addEventListener(
    "click",
    (event) => {
      try {
        onClick(event);
      } catch (error) {
        console.error("cart-recs-tracking select: ", error);
      }
    },
    true
  );
}

try {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
} catch (error) {
  console.error("cart-recs-tracking init: ", error);
}
